import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { Button } from '@/kit/Button';
import { ClosePopup, type ClosePopupData } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { openDialog } from '@/kit/dialog';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { NotificationTextMessage } from '@/core/enums/notification';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import { reportSettingsService } from '@/core/services/reportSettings';
import { CompanyContactType, type CompanyContactRow } from '@/core/models/report-settings.models';
import { partnerAllocationService } from '@/core/services/partnerAllocation';
import { practiceService } from '@/core/services/practice';
import { toIsoDate, type PeriodRow } from '@/core/models/practice.models';
import {
  AllocationBasis,
  formatPartnerMoney,
  toAmount,
  type PartnerOption,
  type ProfitAllocationInput,
  type ProfitAllocationScreen,
  type SavePartnerMemberPayload,
} from '@/core/models/partner-allocation.models';
import { AmountInput } from './AmountInput';
import { decimalPipe, periodLabel } from './format';
import { useMounted } from './useMounted';
import './LeafTables.scss';
import './TaxreturnProfitAllocation.scss';

/** One editable line: the partner or member together with their allocation inputs and the last calculation. */
interface AllocationEditRow {
  key: number;
  relationshipId: string | null;
  fullName: string;
  role: number;
  roleName: string;
  joinedDate: string | null;
  leftDate: string | null;
  sharePercent: number;
  fixedShareUnits: number;
  salaryAmount: number;
  interestOnCapitalRate: number;
  manualAmount: number;
  effectiveDays: number;
  daysInPeriod: number;
  effectivePortion: number;
  allocatedSalary: number;
  allocatedInterest: number;
  allocatedBalance: number;
  roundingAdjustment: number;
  allocatedAmount: number;
  isNew: boolean;
}

type AmountField = 'sharePercent' | 'fixedShareUnits' | 'salaryAmount' | 'interestOnCapitalRate' | 'manualAmount';

const PERIODS_ROUTE = '/home/taxreturns/tasks-accountingperiods';

export function TaxreturnProfitAllocation() {
  const navigate = useNavigate();
  const mounted = useMounted();
  const [client] = useState<SelectedClient | null>(() => selectedClient());
  const nextKey = useRef(1);

  const [screen, setScreen] = useState<ProfitAllocationScreen | null>(null);
  const [rows, setRows] = useState<AllocationEditRow[]>([]);
  const [roles, setRoles] = useState<PartnerOption[]>([]);
  const rolesRef = useRef<PartnerOption[]>([]);
  const [bases, setBases] = useState<PartnerOption[]>([]);
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  /** The client's Business Contacts partners (members), the source of the people to allocate to. */
  const [contacts, setContacts] = useState<CompanyContactRow[]>([]);
  const broughtIn = useRef(new Set<string>());

  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [basis, setBasis] = useState<number>(AllocationBasis.Percentage);
  const [roundingRelationshipId, setRoundingRelationshipId] = useState<string | null>(null);
  /** Blank uses the profit from the period's trial balance. Bound raw (a string once typed), posted as it is. */
  const [profitOverride, setProfitOverride] = useState<number | string | null>(null);
  /** A failed Save marks every row without a name red. */
  const [saveTried, setSaveTried] = useState(false);
  /** In-flight flags: Save and Calculate cannot be sent twice. */
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const entityLabel = screen?.entityLabel ?? 'partner';
  const entityLabelTitle = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
  const isApplicable = screen?.isPartnershipOrLlp === true;
  const canEdit = isApplicable && screen?.isLocked !== true;

  // The partners (members) are the Business Contacts: read them once for a partnership / LLP.
  useEffect(() => {
    if (!client || screen?.isPartnershipOrLlp !== true || contacts.length > 0) return;
    reportSettingsService.getCompanyContacts(client.id).then(
      (view) => {
        if (mounted.current) {
          setContacts((view?.contacts ?? []).filter((c) => c.contactType === CompanyContactType.Director));
        }
      },
      () => undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, screen?.isPartnershipOrLlp]);

  // Every Business Contacts partner not yet in the list is added as a new row (name and dates filled in), so the user
  // only sets the shares and saves. Matched by name, so a saved partner is never added twice.
  useEffect(() => {
    if (!canEdit || contacts.length === 0) return;
    setRows((current) => {
      const names = new Set(current.map((r) => r.fullName.trim().toLowerCase()));
      const missing = contacts.filter(
        (c) => !names.has(c.fullName.trim().toLowerCase()) && !broughtIn.current.has(c.id),
      );
      if (missing.length === 0) return current;
      missing.forEach((c) => broughtIn.current.add(c.id));
      return [
        ...current,
        ...missing.map((c): AllocationEditRow => ({
          key: nextKey.current++,
          relationshipId: null,
          fullName: c.fullName,
          role: rolesRef.current.length > 0 ? rolesRef.current[0].id : 1,
          roleName: rolesRef.current.length > 0 ? rolesRef.current[0].name : '',
          joinedDate: c.dateAppointed,
          leftDate: c.dateCeased,
          sharePercent: 0,
          fixedShareUnits: 0,
          salaryAmount: 0,
          interestOnCapitalRate: 0,
          manualAmount: 0,
          effectiveDays: 0,
          daysInPeriod: 0,
          effectivePortion: 0,
          allocatedSalary: 0,
          allocatedInterest: 0,
          allocatedBalance: 0,
          roundingAdjustment: 0,
          allocatedAmount: 0,
          isNew: true,
        })),
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, screen, canEdit]);

  const apply = useCallback(
    (next: ProfitAllocationScreen) => {
      // A fresh load shows what is saved; contacts still missing from it are brought in again below.
      broughtIn.current.clear();
      setScreen(next);
      setBases(next.bases);
      setBasis(next.basis);
      setRoundingRelationshipId(next.roundingRelationshipId);
      setSelectedPeriodId(next.accountingPeriodId);
      setRows(
        next.rows.map((r) => ({
          key: nextKey.current++,
          relationshipId: r.relationshipId,
          fullName: r.fullName,
          role: r.role,
          roleName: r.roleName,
          joinedDate: r.joinedDate,
          leftDate: r.leftDate,
          sharePercent: r.sharePercent,
          fixedShareUnits: r.fixedShareUnits,
          salaryAmount: r.salaryAmount,
          interestOnCapitalRate: r.interestOnCapitalRate,
          manualAmount: r.manualAmount,
          effectiveDays: r.effectiveDays,
          daysInPeriod: r.daysInPeriod,
          effectivePortion: r.effectivePortion,
          allocatedSalary: r.allocatedSalary,
          allocatedInterest: r.allocatedInterest,
          allocatedBalance: r.allocatedBalance,
          roundingAdjustment: r.roundingAdjustment,
          allocatedAmount: r.allocatedAmount,
          isNew: false,
        })),
      );

      if (next.isPartnershipOrLlp && rolesRef.current.length === 0 && client) {
        partnerAllocationService
          .getPartners(client.id)
          .then((view) => {
            if (!mounted.current) return;
            rolesRef.current = view.roles;
            setRoles(view.roles);
          })
          .catch(() => {
            if (!mounted.current) return;
            rolesRef.current = [];
            setRoles([]);
          });
      }
    },
    [client, mounted],
  );

  const load = useCallback(
    (periodId: string | null) => {
      if (!client) return;
      partnerAllocationService
        .getAllocation(client.id, periodId)
        .then((result) => {
          if (mounted.current) apply(result);
        })
        .catch((error) => {
          if (mounted.current) notifyError(error);
        });
    },
    [client, apply, mounted],
  );

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    practiceService
      .getClientPeriods(client.id)
      .then((result) => {
        if (mounted.current) setPeriods(result.periods ?? []);
      })
      .catch(() => {
        if (mounted.current) setPeriods([]);
      });
    load(null);
    // Runs once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const money = (value: number) => formatPartnerMoney(value);

  const updateRow = (key: number, patch: Partial<AllocationEditRow>) =>
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () => {
    if (!canEdit) return;
    setRows((current) => [
      ...current,
      {
        key: nextKey.current++,
        relationshipId: null,
        fullName: '',
        role: roles.length > 0 ? roles[0].id : 1,
        roleName: roles.length > 0 ? roles[0].name : '',
        joinedDate: null,
        leftDate: null,
        sharePercent: 0,
        fixedShareUnits: 0,
        salaryAmount: 0,
        interestOnCapitalRate: 0,
        manualAmount: 0,
        effectiveDays: 0,
        daysInPeriod: 0,
        effectivePortion: 0,
        allocatedSalary: 0,
        allocatedInterest: 0,
        allocatedBalance: 0,
        roundingAdjustment: 0,
        allocatedAmount: 0,
        isNew: true,
      },
    ]);
  };

  const onDelete = (row: AllocationEditRow) => {
    if (!row.relationshipId) {
      setRows((current) => current.filter((r) => r.key !== row.key));
      return;
    }
    const relationshipId = row.relationshipId;
    openDialog<ClosePopupData, boolean>(ClosePopup, { data: { itemName: entityLabelTitle, count: 1 } })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) return;
        partnerAllocationService
          .deletePartner(relationshipId)
          .then(() => {
            if (!mounted.current) return;
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            load(selectedPeriodId);
          })
          .catch((error) => {
            if (mounted.current) notifyError(error);
          });
      });
  };

  /** `savedIds` carries the id of every person just saved (a new person has none until then), by row key. */
  const saveAllocationInputs = (savedIds: Map<number, string>) => {
    const periodId = screen?.accountingPeriodId;
    if (!client || !periodId) {
      setSaving(false);
      load(selectedPeriodId);
      return;
    }

    const inputs: ProfitAllocationInput[] = rows
      .map((r) => ({ row: r, relationshipId: r.relationshipId ?? savedIds.get(r.key) ?? null }))
      .filter((r): r is { row: AllocationEditRow; relationshipId: string } => !!r.relationshipId)
      .map(({ row: r, relationshipId }) => ({
        relationshipId,
        sharePercent: toAmount(r.sharePercent),
        fixedShareUnits: toAmount(r.fixedShareUnits),
        salaryAmount: toAmount(r.salaryAmount),
        interestOnCapitalRate: toAmount(r.interestOnCapitalRate),
        manualAmount: toAmount(r.manualAmount),
      }));

    partnerAllocationService
      .saveAllocation({
        clientId: client.id,
        accountingPeriodId: periodId,
        basis,
        roundingRelationshipId,
        rows: inputs,
      })
      .then((result) => {
        if (!mounted.current) return;
        apply(result);
        notifySuccess(NotificationTextMessage.successMessage);
      })
      .catch((error) => {
        if (!mounted.current) return;
        notifyError(error);
        load(selectedPeriodId);
      })
      .finally(() => {
        if (mounted.current) setSaving(false);
      });
  };

  /** Saves the people and their allocation inputs; it does not calculate. */
  const save = () => {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    if (!canEdit) return;

    const invalid = rows.find((r) => r.fullName.trim().length === 0);
    if (invalid) {
      setSaveTried(true);
      notifyError(`Enter the name of every ${entityLabel}.`);
      return;
    }
    // Joined and Left are a pair: someone cannot leave before they joined.
    for (const r of rows) {
      const problem = dateRangeProblem(r.joinedDate, r.leftDate, `The time ${r.fullName.trim()} was a ${entityLabel}`);
      if (problem) {
        notifyError(problem);
        return;
      }
    }
    setSaving(true);

    const clientId = client.id;
    const payloads: SavePartnerMemberPayload[] = rows.map((row) => ({
      clientId,
      id: row.relationshipId,
      fullName: row.fullName.trim(),
      role: row.role,
      joinedDate: toIsoDate(row.joinedDate),
      leftDate: toIsoDate(row.leftDate),
      profitSharePercent: toAmount(row.sharePercent),
      fixedShareUnits: toAmount(row.fixedShareUnits),
      salaryAmount: toAmount(row.salaryAmount),
      interestOnCapitalRate: toAmount(row.interestOnCapitalRate),
      utr: null,
      addressLine: null,
      city: null,
      postCode: null,
    }));

    // Saved one at a time so a validation failure names the row that caused it.
    const savedIds = new Map<number, string>();
    const saveNext = (index: number): void => {
      if (index >= payloads.length) {
        saveAllocationInputs(savedIds);
        return;
      }
      partnerAllocationService
        .savePartner(payloads[index])
        .then((saved) => {
          if (!mounted.current) return;
          if (saved?.id) savedIds.set(rows[index].key, saved.id);
          saveNext(index + 1);
        })
        .catch((error) => {
          if (!mounted.current) return;
          setSaving(false);
          notifyError(error);
        });
    };

    saveNext(0);
  };

  const calculate = () => {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    if (!canEdit) return;
    const periodId = screen?.accountingPeriodId;
    if (!periodId) {
      notifyError('This client has no accounting period to allocate.');
      return;
    }

    setCalculating(true);
    partnerAllocationService
      .calculateAllocation({
        clientId: client.id,
        accountingPeriodId: periodId,
        profitOverride: profitOverride as number | null,
      })
      .then((result) => {
        if (!mounted.current) return;
        apply(result);
        notifySuccess('The allocation reconciles to the profit available.');
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      })
      .finally(() => {
        if (mounted.current) setCalculating(false);
      });
  };

  const amountColumn = (
    id: string,
    header: string,
    width: string,
    field: AmountField,
    disabledWhen?: () => boolean,
  ): DataTableColumn<AllocationEditRow> => ({
    id,
    header,
    headerClassName: width,
    cell: (row) => (
      <AmountInput
        fieldClassName={width}
        disabled={!canEdit || (disabledWhen?.() ?? false)}
        value={row[field]}
        onChange={(value) => updateRow(row.key, { [field]: value })}
      />
    ),
  });

  const columns: DataTableColumn<AllocationEditRow>[] = [
    {
      id: 'name',
      header: (
        <>
          {entityLabelTitle} <RequiredMark />
        </>
      ),
      headerClassName: 'w-200',
      cell: (row) => (
        <InputField
          fieldClassName="w-180"
          autoComplete="off"
          maxLength={255}
          charMode="text"
          invalid={saveTried && row.fullName.trim().length === 0}
          disabled={!canEdit}
          value={row.fullName}
          onChange={(event) => updateRow(row.key, { fullName: event.target.value })}
        />
      ),
    },
    {
      id: 'role',
      header: 'Role',
      headerClassName: 'w-150',
      cell: (row) => (
        <Select
          className="w-150"
          panelClass="myPanelClass"
          items={roles}
          bindLabel="name"
          bindValue="id"
          clearable={false}
          searchable={false}
          disabled={!canEdit}
          value={row.role}
          onChange={(value) => updateRow(row.key, { role: value as number })}
        />
      ),
    },
    {
      id: 'joined',
      header: 'Joined',
      headerClassName: 'w-150',
      cell: (row) => (
        <DateField
          fieldClassName="w-150"
          readOnly
          disabled={!canEdit}
          value={row.joinedDate}
          onChange={(value) => updateRow(row.key, { joinedDate: value as unknown as string | null })}
        />
      ),
    },
    {
      id: 'left',
      header: 'Left',
      headerClassName: 'w-150',
      cell: (row) => (
        <DateField
          fieldClassName="w-150"
          readOnly
          disabled={!canEdit}
          min={row.joinedDate && dayjs(row.joinedDate).isValid() ? dayjs(row.joinedDate) : undefined}
          invalid={dateRangeProblem(row.joinedDate, row.leftDate) !== null}
          value={row.leftDate}
          onChange={(value) => updateRow(row.key, { leftDate: value as unknown as string | null })}
        />
      ),
    },
    amountColumn('share', 'Share %', 'w-100', 'sharePercent'),
    amountColumn('fixed', 'Fixed share', 'w-100', 'fixedShareUnits'),
    amountColumn('salary', 'Salary', 'w-120', 'salaryAmount'),
    amountColumn('rate', 'Interest %', 'w-100', 'interestOnCapitalRate'),
    amountColumn('manual', 'Manual amount', 'w-120', 'manualAmount', () => basis !== AllocationBasis.Manual),
    {
      id: 'effective',
      header: 'Days in period',
      headerClassName: 'w-120',
      cell: (row) => (
        <div className="white-space-nowrap">
          {row.daysInPeriod > 0 ? (
            <span>
              {row.effectiveDays} / {row.daysInPeriod} ({decimalPipe(row.effectivePortion, 0, 2)}%)
            </span>
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
    {
      id: 'allocated',
      header: 'Allocated',
      headerClassName: 'w-150',
      cell: (row) => (
        <div className="white-space-nowrap">
          {money(row.allocatedAmount)}{' '}
          {row.roundingAdjustment ? <span>(includes rounding {money(row.roundingAdjustment)})</span> : null}
        </div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      headerClassName: 'w-80',
      cell: (row) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Delete" disabled={!canEdit} onClick={() => onDelete(row)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-profit-allocation">
      {/* Design review 2 Nov 2026: Save and Close are page actions, so they sit top-right in the sticky page header. */}
      <PageHeader title="Profit Allocation">
        {screen && isApplicable && (
          <Button className="action-button primary-button border-radius-6" disabled={!canEdit} busy={saving} onClick={save}>
            <span>Save</span>
          </Button>
        )}
        <Button className="action-button secondary-button" onClick={() => navigate(PERIODS_ROUTE)}>
          <span>Close</span>
        </Button>
      </PageHeader>
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select<PeriodRow>
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              items={periods}
              bindValue="id"
              clearable={false}
              searchable={false}
              placeholder="Accounting period"
              value={selectedPeriodId}
              onChange={(value) => {
                const periodId = value as string | null;
                setSelectedPeriodId(periodId);
                load(periodId);
              }}
              renderLabel={periodLabel}
              renderOption={periodLabel}
            />
          </div>
        </div>

        {/* Partnerships and LLPs only. */}
        {screen && !isApplicable && (
          <div className="not-applicable">
            <p>
              Profit allocation applies to partnerships and LLPs only. <strong>{screen.clientName}</strong> is not one
              of those, so there are no partners or members to allocate profit to. Change the client type in Manage
              Clients, or select a partnership or LLP client.
            </p>
          </div>
        )}

        {screen && isApplicable && (
          <>
            <div className="d-flex align-items-center reconciliation-bar mb-1">
              <div>
                <strong>Period:</strong> {screen.periodLabel || 'No accounting period'}
              </div>
              <div>
                <strong>Profit available:</strong> {money(screen.profitAvailable)}
              </div>
              <div>
                <strong>Allocated:</strong> {money(screen.totalAllocated)}
              </div>
              <div>
                <strong>Difference:</strong> {money(screen.difference)}
              </div>
              <div className={`status-pill ${screen.isReconciled ? 'reconciled' : 'not-reconciled'}`}>
                {screen.reconciliationStatus}
              </div>
            </div>

            <div className="d-flex align-items-center reconciliation-bar mb-1">
              <div className="form-field">
                <p>Allocation basis</p>
                <Select<PartnerOption>
                  className="w-300"
                  openOnFocus
                  panelClass="myPanelClass"
                  items={bases}
                  bindLabel="name"
                  bindValue="id"
                  clearable={false}
                  searchable={false}
                  disabled={!canEdit}
                  value={basis}
                  onChange={(value) => setBasis(value as number)}
                />
              </div>
              <div className="form-field">
                <p>{entityLabelTitle} carrying any rounding</p>
                <Select
                  className="w-250"
                  openOnFocus
                  panelClass="myPanelClass"
                  items={screen.rows}
                  bindLabel="fullName"
                  bindValue="relationshipId"
                  clearable
                  placeholder="Largest share"
                  disabled={!canEdit}
                  value={roundingRelationshipId}
                  onChange={(value) => setRoundingRelationshipId(value as string | null)}
                />
              </div>
              <div className="form-field">
                <p>Profit to allocate (blank uses the trial balance)</p>
                <InputField
                  fieldClassName="w-180"
                  autoComplete="off"
                  inputMode="decimal"
                  charMode="number"
                  allowDecimal
                  allowNegative
                  maxIntDigits={15}
                  maxDecimals={4}
                  disabled={!canEdit}
                  value={profitOverride ?? ''}
                  onChange={(event) => setProfitOverride(event.target.value)}
                />
              </div>
              <div>
                <Button className="action-button primary-button border-radius-6" disabled={!canEdit} onClick={addRow}>
                  <Icon name="add" />
                  <span>Add {entityLabel}</span>
                </Button>
                <Button
                  className="action-button primary-button border-radius-6 ml-1"
                  disabled={!canEdit}
                  busy={calculating}
                  onClick={calculate}
                >
                  <span>Calculate</span>
                </Button>
              </div>
            </div>

            {!screen.hasTrialBalance && (
              <p>This period has no trial balance, so there is no profit or loss to allocate yet.</p>
            )}
            {screen.isLocked && (
              <p>The accounting period is locked. Unlock it from Accounting Periods before changing the allocation.</p>
            )}

            <div className="mat-table-wrapper">
              <DataTable
                stickyHeader
                className="tr-leaf-sticky-header"
                columns={columns}
                rows={rows}
                rowKey={(row) => row.key}
              />
            </div>
          </>
        )}
      </div>
    </SideMenuPage>
  );
}
